import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Books, Plus, PencilSimple, Trash, X, MagnifyingGlass, ArrowRight,
  Check, ArrowUp, ArrowDown, EyeSlash, Eye,
} from "@phosphor-icons/react";

/**
 * CollectionsManager — an inline panel used inside ExpertDashboard.
 * Lets an approved expert create, edit, delete, and populate collections
 * with their own approved posts. Consumed only by ExpertDashboard.
 */
export default function CollectionsManager({ expertSlug, ownPosts }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    http.get("/experts/me/collections")
      .then(({ data }) => setCollections(data.collections || []))
      .catch(() => setCollections([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <section className="rounded-2xl bg-white border border-black/10 p-6 shadow-sm" data-testid="dashboard-collections">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif-display text-2xl flex items-center gap-2">
          <Books size={20} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Collections
        </h2>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="dashboard-create-collection"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
        >
          <Plus size={11} weight="bold" /> New collection
        </button>
      </div>
      <p className="mt-1 text-sm text-[#3A3830]">
        Bundle your best observations and articles into curated collections readers can explore end-to-end.
      </p>

      <div className="mt-4 space-y-3">
        {loading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-24 rounded-lg bg-[#FBF2E1]/40 animate-pulse" />)}
        {!loading && collections.length === 0 && (
          <div className="text-sm text-[#3A3830] py-4">No collections yet. Create one to start curating.</div>
        )}
        {collections.map((c) => (
          <div key={c.id} data-testid={`collection-card-${c.id}`} className="rounded-lg border border-black/10 p-4 bg-[#FBF2E1]/40 flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
                <Books size={11} weight="fill" /> {c.items_count} {c.items_count === 1 ? "story" : "stories"}
                {c.topic && <><span className="text-[#3A3830]">·</span><span>{c.topic}</span></>}
                {!c.is_public && <><span className="text-[#3A3830]">·</span><span className="inline-flex items-center gap-1 text-amber-800"><EyeSlash size={10} weight="fill" /> private</span></>}
              </div>
              <div className="mt-1 font-serif-display text-lg leading-tight">{c.title}</div>
              {c.description && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{c.description}</div>}
              <div className="mt-1 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
                {c.view_count || 0} views
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/collections/${c.id}`}
                data-testid={`collection-view-${c.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
              >
                View <ArrowRight size={10} weight="bold" />
              </Link>
              <button
                onClick={() => setEditing(c)}
                data-testid={`collection-edit-${c.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
              >
                <PencilSimple size={11} weight="fill" /> Edit
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
                  try {
                    await http.delete(`/experts/collections/${c.id}`);
                    toast.success("Collection deleted");
                    reload();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
                }}
                data-testid={`collection-delete-${c.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"
              >
                <Trash size={11} weight="fill" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <CollectionCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); }}
        />
      )}
      {editing && (
        <CollectionEditModal
          collection={editing}
          ownPosts={ownPosts}
          onClose={() => setEditing(null)}
          onUpdated={() => { setEditing(null); reload(); }}
        />
      )}
    </section>
  );
}

function CollectionCreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [topic, setTopic] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (title.trim().length < 3) return toast.error("Title must be at least 3 characters.");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        topic: topic.trim() || undefined,
        cover_image: coverImage.trim() || undefined,
        is_public: isPublic,
      };
      await http.post("/experts/me/collections", payload);
      toast.success("Collection created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" data-testid="collection-create-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl text-[#1B4332]">
        <div className="flex items-center justify-between">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">New collection</div>
          <button onClick={onClose} data-testid="collection-create-close" className="rounded-full hover:bg-black/5 p-1"><X size={16} weight="bold" /></button>
        </div>
        <h3 className="mt-1 font-serif-display text-2xl leading-tight">Curate a new collection</h3>
        <div className="mt-4 space-y-3">
          <Field label="Title *">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Butterflies of Kerala"
              data-testid="collection-title-input"
              className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short introduction — who, what, why."
              data-testid="collection-description-input"
              className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Topic">
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Biodiversity"
                data-testid="collection-topic-input"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Cover image URL">
              <input
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="https://…"
                data-testid="collection-cover-input"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[#3A3830]">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="collection-public-input" />
            Public — anyone can browse this collection
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
          <button
            onClick={create}
            disabled={saving}
            data-testid="collection-create-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CollectionEditModal({ collection, ownPosts, onClose, onUpdated }) {
  const [title, setTitle] = useState(collection.title || "");
  const [description, setDescription] = useState(collection.description || "");
  const [topic, setTopic] = useState(collection.topic || "");
  const [coverImage, setCoverImage] = useState(collection.cover_image || "");
  const [isPublic, setIsPublic] = useState(collection.is_public !== false);
  const [items, setItems] = useState([]);
  const [itemIds, setItemIds] = useState(collection.item_ids || []);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // Reload the collection to get the items in order along with full post data.
  useEffect(() => {
    http.get(`/collections/${collection.id}`)
      .then(({ data }) => {
        const arr = data?.items || [];
        setItems(arr);
        setItemIds(arr.map((i) => i.id));
      })
      .catch(() => {});
  }, [collection.id]);

  const filteredCandidates = (ownPosts || []).filter((p) => {
    if (itemIds.includes(p.id)) return false;
    if (!search) return true;
    const hay = `${p.title || ""} ${p.headline || ""} ${p.summary || ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  }).slice(0, 20);

  const addItem = async (id) => {
    try {
      await http.post(`/experts/collections/${collection.id}/items`, { story_id: id });
      const post = (ownPosts || []).find((p) => p.id === id);
      if (post) setItems((cur) => [...cur, post]);
      setItemIds((cur) => [...cur, id]);
      toast.success("Added to collection");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const removeItem = async (id) => {
    try {
      await http.delete(`/experts/collections/${collection.id}/items/${id}`);
      setItems((cur) => cur.filter((i) => i.id !== id));
      setItemIds((cur) => cur.filter((i) => i !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    const [row] = next.splice(idx, 1);
    next.splice(j, 0, row);
    setItems(next);
    const nextIds = next.map((n) => n.id);
    setItemIds(nextIds);
    try {
      await http.put(`/experts/collections/${collection.id}/reorder`, { item_ids: nextIds });
    } catch (e) { toast.error("Could not reorder"); }
  };

  const saveMeta = async () => {
    if (title.trim().length < 3) return toast.error("Title must be at least 3 characters.");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        topic: topic.trim() || null,
        cover_image: coverImage.trim() || null,
        is_public: isPublic,
      };
      await http.patch(`/experts/collections/${collection.id}`, payload);
      toast.success("Collection updated");
      onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" data-testid="collection-edit-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl text-[#1B4332]">
        <div className="flex items-center justify-between sticky top-0 bg-white pb-2 -mt-1 pt-1">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Edit collection</div>
          <button onClick={onClose} data-testid="collection-edit-close" className="rounded-full hover:bg-black/5 p-1"><X size={16} weight="bold" /></button>
        </div>
        <h3 className="mt-1 font-serif-display text-2xl leading-tight">{collection.title}</h3>

        {/* Meta editor */}
        <div className="mt-4 rounded-xl border border-black/10 p-4 bg-[#FBF2E1]/30">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Details</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title *">
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="collection-edit-title" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="Topic">
              <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="collection-edit-topic" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Description">
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="collection-edit-description" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Cover image URL">
              <input value={coverImage} onChange={(e) => setCoverImage(e.target.value)} data-testid="collection-edit-cover" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-[#3A3830]">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="collection-edit-public" />
              {isPublic ? <><Eye size={12} weight="fill" /> Public</> : <><EyeSlash size={12} weight="fill" /> Private</>}
            </label>
            <button
              onClick={saveMeta}
              disabled={saving}
              data-testid="collection-edit-save"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
            >
              <Check size={11} weight="bold" /> {saving ? "Saving…" : "Save details"}
            </button>
          </div>
        </div>

        {/* Items list */}
        <div className="mt-5">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
            {items.length} stories in this collection · drag to reorder
          </div>
          <div className="mt-2 space-y-2">
            {items.length === 0 && (
              <div className="text-sm text-[#3A3830] rounded-lg border border-dashed border-black/20 p-4 bg-white/60">
                No stories yet. Add from your library below.
              </div>
            )}
            {items.map((it, idx) => (
              <div key={it.id} data-testid={`collection-item-row-${it.id}`} className="rounded-lg border border-black/10 p-3 bg-white flex items-start gap-3">
                <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830] pt-1">#{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-sm leading-tight">{it.title || it.headline || "Untitled"}</div>
                  <div className="text-[10px] text-[#3A3830] mt-0.5">{(it.story_type || "").replace(/_/g, " ")}{it.category ? ` · ${it.category}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`collection-item-up-${it.id}`} className="rounded-full border border-black/15 hover:bg-black/5 disabled:opacity-30 p-1"><ArrowUp size={11} weight="bold" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`collection-item-down-${it.id}`} className="rounded-full border border-black/15 hover:bg-black/5 disabled:opacity-30 p-1"><ArrowDown size={11} weight="bold" /></button>
                  <button onClick={() => removeItem(it.id)} data-testid={`collection-item-remove-${it.id}`} className="rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 p-1"><X size={11} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Candidate library */}
        <div className="mt-5">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Add from your library</div>
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-black/15 bg-white px-3 py-2">
            <MagnifyingGlass size={13} weight="bold" className="text-[#3A3830]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your posts…"
              data-testid="collection-search-input"
              className="flex-1 bg-transparent text-sm outline-none"
            />
          </div>
          <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
            {filteredCandidates.length === 0 && (
              <div className="text-xs text-[#3A3830] p-2">
                {ownPosts?.length ? "No matching posts." : "No posts yet — publish something first."}
              </div>
            )}
            {filteredCandidates.map((p) => (
              <button
                key={p.id}
                onClick={() => addItem(p.id)}
                data-testid={`collection-add-candidate-${p.id}`}
                className="w-full text-left rounded-lg border border-black/10 p-3 bg-white hover:border-[color:var(--np-brand,#B85F2A)] flex items-start gap-2 group"
              >
                <Plus size={12} weight="bold" className="mt-0.5 text-[color:var(--np-brand,#B85F2A)] group-hover:scale-110 transition-transform" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-sm leading-tight line-clamp-1">{p.title || p.headline || "Untitled"}</div>
                  <div className="text-[10px] text-[#3A3830] mt-0.5">
                    {(p.story_type || "").replace(/_/g, " ")}{p.category ? ` · ${p.category}` : ""}
                    {p.moderation_status && p.moderation_status !== "approved" && (
                      <span className="ml-2 text-amber-700">· {p.moderation_status}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
